import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Globe,
  Loader2,
  Pencil,
  Search,
  Shield,
  Trash2,
  UserCircle,
  UserPlus,
  X,
  XCircle,
} from 'lucide-react';
import { api, type AdminUser, type AdminUserCognitoAuthEvent } from '../lib/api';
import { useAuth } from '../contexts/use-auth';
import type { AppRole } from '../contexts/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import {
  computeEffectivePermissions,
  defaultPermissionsFor,
  PERMISSION_CATALOG,
  PERMISSION_GROUPS,
  isPermission,
  type Permission,
  type PermissionDef,
} from '../lib/permissions';
import { formatDateTime, formatRelative } from '../lib/format';
import { cn } from '../lib/utils';

const ROLE_OPTIONS: AppRole[] = ['Admin', 'SafetyNet'];

const getAdminUserValue = (item: AdminUser, key: string): unknown => {
  switch (key) {
    case 'email':
      return item.email;
    case 'role':
      return item.role;
    case 'addedBy':
      return item.addedBy;
    case 'updatedAt':
      return item.updatedAt;
    case 'overrides':
      return (item.grantedPermissions?.length ?? 0) + (item.deniedPermissions?.length ?? 0);
    default:
      return undefined;
  }
};

export function AdminUsers() {
  const queryClient = useQueryClient();
  const { email: currentEmail, can } = useAuth();
  const canWrite = can('admin.users.write');

  const list = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.listAdminUsers(),
  });

  const allItems = useMemo(() => list.data?.items ?? [], [list.data]);

  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q.trim()) return allItems;
    const needle = q.toLowerCase();
    return allItems.filter(
      (u) =>
        u.email.toLowerCase().includes(needle) ||
        u.role.toLowerCase().includes(needle) ||
        u.addedBy.toLowerCase().includes(needle),
    );
  }, [allItems, q]);

  const { sortedItems, sort, toggle } = useSort(filtered, getAdminUserValue);

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<AppRole>('SafetyNet');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [opened, setOpened] = useState<AdminUser | null>(null);

  const addMutation = useMutation({
    mutationFn: () => api.addAdminUser(newEmail.trim().toLowerCase(), newRole),
    onSuccess: () => {
      setNewEmail('');
      setNewRole('SafetyNet');
      setErrorMessage(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => setErrorMessage((err as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: (email: string) => api.deleteAdminUser(email),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
    onError: (err) => setErrorMessage((err as Error).message),
  });

  function downloadCsv() {
    const rows = sortedItems;
    const headers = [
      'email',
      'role',
      'addedBy',
      'createdAt',
      'updatedAt',
      'grantedPermissions',
      'deniedPermissions',
    ];
    const csvLines = [headers.join(',')];
    for (const r of rows) {
      csvLines.push(
        [
          csvEscape(r.email),
          csvEscape(r.role),
          csvEscape(r.addedBy),
          csvEscape(r.createdAt),
          csvEscape(r.updatedAt),
          csvEscape((r.grantedPermissions ?? []).join(';')),
          csvEscape((r.deniedPermissions ?? []).join(';')),
        ].join(','),
      );
    }
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `x5-admin-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Admin Users</h1>
          <p className="text-muted-foreground">
            Control who can sign in and what they can do. Sign-in is locked to{' '}
            <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">@xiltrixusa.com</code>{' '}
            emails by the Cognito pre-sign-up trigger.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, role…"
              className="pl-9"
            />
          </div>
          <Button variant="outline" onClick={downloadCsv} disabled={sortedItems.length === 0}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {canWrite && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-muted-foreground" />
              Add user
            </CardTitle>
            <CardDescription>Grants Google sign-in access at the chosen role.</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="flex gap-3 items-end flex-wrap"
              onSubmit={(e) => {
                e.preventDefault();
                addMutation.mutate();
              }}
            >
              <div className="flex-1 min-w-[260px]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Email
                </label>
                <Input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="someone@xiltrixusa.com"
                />
              </div>
              <div className="w-40">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Role</label>
                <Select value={newRole} onChange={(e) => setNewRole(e.target.value as AppRole)}>
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </div>
              <Button type="submit" disabled={addMutation.isPending || !newEmail.includes('@')}>
                {addMutation.isPending ? 'Adding…' : 'Add user'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {errorMessage && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3">
          {errorMessage}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <SortableHeader label="Email" sortKey="email" sort={sort} onToggle={toggle} />
                <SortableHeader label="Role" sortKey="role" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Overrides"
                  sortKey="overrides"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader
                  label="Added by"
                  sortKey="addedBy"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader
                  label="Updated"
                  sortKey="updatedAt"
                  sort={sort}
                  onToggle={toggle}
                />
                <th className="px-4 py-3 font-medium w-1"></th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {sortedItems.map((u) => {
                const isSelf = u.email === currentEmail;
                const grants = u.grantedPermissions?.length ?? 0;
                const denies = u.deniedPermissions?.length ?? 0;
                return (
                  <tr
                    key={u.email}
                    onClick={() => setOpened(u)}
                    className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium">
                      {u.email}
                      {isSelf && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          you
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={u.role === 'Admin' ? 'admin' : 'safetynet'}>{u.role}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {grants === 0 && denies === 0 ? (
                        <span>—</span>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          {grants > 0 && (
                            <Badge variant="success" className="text-[10px]">
                              +{grants}
                            </Badge>
                          )}
                          {denies > 0 && (
                            <Badge variant="destructive" className="text-[10px]">
                              −{denies}
                            </Badge>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.addedBy}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(u.updatedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {canWrite && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpened(u);
                            }}
                            title="Edit permissions"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isSelf || deleteMutation.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Remove ${u.email}?`)) deleteMutation.mutate(u.email);
                            }}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {list.data && sortedItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    {q.trim() ? 'No matching users.' : 'No admin users yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {opened && (
        <UserDetailDrawer
          user={opened}
          onClose={() => setOpened(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setOpened(null);
          }}
        />
      )}
    </div>
  );
}

function UserDetailDrawer({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { email: currentEmail, can } = useAuth();
  const isSelf = user.email === currentEmail;
  const canWrite = can('admin.users.write');

  const cognito = useQuery({
    queryKey: ['admin-users', 'cognito', user.email],
    queryFn: () => api.getAdminUserCognito(user.email),
    staleTime: 60 * 1000,
  });

  const [role, setRole] = useState<AppRole>(user.role);
  const [granted, setGranted] = useState<Set<Permission>>(
    () => new Set((user.grantedPermissions ?? []).filter(isPermission)),
  );
  const [denied, setDenied] = useState<Set<Permission>>(
    () => new Set((user.deniedPermissions ?? []).filter(isPermission)),
  );
  const [error, setError] = useState<string | null>(null);

  const defaults = defaultPermissionsFor(role);
  const effective = useMemo(
    () =>
      computeEffectivePermissions(role, Array.from(granted), Array.from(denied)),
    [role, granted, denied],
  );

  const mutation = useMutation({
    mutationFn: () =>
      api.updateAdminUser(user.email, {
        role,
        grantedPermissions: Array.from(granted).sort(),
        deniedPermissions: Array.from(denied).sort(),
      }),
    onSuccess: () => onSaved(),
    onError: (err) => setError((err as Error).message),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, PermissionDef[]>();
    for (const def of PERMISSION_CATALOG) {
      const list = map.get(def.group) ?? [];
      list.push(def);
      map.set(def.group, list);
    }
    return map;
  }, []);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-card border-l border-border shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold truncate">{user.email}</h2>
            <p className="text-xs text-muted-foreground">
              {canWrite ? 'View identity + edit role and permissions' : 'View identity and permissions'}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <CognitoSection cognitoQuery={cognito} />

          {isSelf && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-700 px-4 py-3 text-sm">
              This is your own account. Nobody can change their own role or permissions — ask
              another Admin. You can still view your identity and effective permissions here.
            </div>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Role</CardTitle>
              <CardDescription>
                The role establishes the default permission set. Per-permission overrides below add
                to or subtract from this baseline.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value as AppRole)}
                disabled={isSelf || !canWrite}
                className="w-48"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Permissions</CardTitle>
              <CardDescription>
                A checkbox lit means the user <span className="font-medium">has</span> that
                permission. Defaults from the role are pre-checked. Toggling a default
                <span className="font-medium"> off</span> creates a denial; toggling a non-default
                <span className="font-medium"> on</span> creates a grant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {PERMISSION_GROUPS.map((group) => (
                <div key={group}>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    {group}
                  </div>
                  <ul className="space-y-2">
                    {(grouped.get(group) ?? []).map((def) => {
                      const isDefault = defaults.has(def.key);
                      const isGranted = granted.has(def.key);
                      const isDenied = denied.has(def.key);
                      const checked = effective.has(def.key);
                      const overrideType: 'grant' | 'deny' | null = isGranted
                        ? 'grant'
                        : isDenied
                          ? 'deny'
                          : null;

                      return (
                        <li key={def.key} className="flex gap-3 items-start">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canWrite || isSelf}
                            onChange={() => {
                              setError(null);
                              const newGranted = new Set(granted);
                              const newDenied = new Set(denied);
                              if (checked) {
                                if (isDefault) {
                                  newGranted.delete(def.key);
                                  newDenied.add(def.key);
                                } else {
                                  newGranted.delete(def.key);
                                  newDenied.delete(def.key);
                                }
                              } else {
                                if (isDefault) {
                                  newDenied.delete(def.key);
                                  newGranted.delete(def.key);
                                } else {
                                  newGranted.add(def.key);
                                  newDenied.delete(def.key);
                                }
                              }
                              setGranted(newGranted);
                              setDenied(newDenied);
                            }}
                            className="mt-1 h-4 w-4 accent-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="font-mono text-xs text-foreground">{def.key}</code>
                              <span className="text-sm font-medium">{def.label}</span>
                              {def.reserved && (
                                <Badge variant="outline" className="text-[10px]">
                                  reserved
                                </Badge>
                              )}
                              {overrideType === 'grant' && (
                                <Badge variant="success" className="text-[10px]">
                                  grant
                                </Badge>
                              )}
                              {overrideType === 'deny' && (
                                <Badge variant="destructive" className="text-[10px]">
                                  deny
                                </Badge>
                              )}
                              {isDefault && !isDenied && !isGranted && (
                                <Badge variant="outline" className="text-[10px]">
                                  default
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {def.description}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-border pt-4">
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{effective.size}</span> permissions
              active ·{' '}
              <span className={cn(granted.size > 0 && 'text-foreground font-medium')}>
                {granted.size} grant{granted.size === 1 ? '' : 's'}
              </span>{' '}
              ·{' '}
              <span className={cn(denied.size > 0 && 'text-foreground font-medium')}>
                {denied.size} denial{denied.size === 1 ? '' : 's'}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                {canWrite ? 'Cancel' : 'Close'}
              </Button>
              {canWrite && !isSelf && (
                <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                  {mutation.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CognitoSection({
  cognitoQuery,
}: {
  cognitoQuery: ReturnType<typeof useQuery<import('../lib/api').AdminUserCognito>>;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <UserCircle className="h-4 w-4 text-muted-foreground" />
          Identity (Cognito)
        </CardTitle>
        <CardDescription>
          What Cognito knows about this user. Pre-provisioned users won't have a record here until
          they actually sign in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {cognitoQuery.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Looking up Cognito record…
          </div>
        )}
        {cognitoQuery.error && (
          <div className="text-sm text-destructive">
            {(cognitoQuery.error as Error).message}
          </div>
        )}
        {cognitoQuery.data && !cognitoQuery.data.exists && (
          <div className="rounded-md border border-warning/40 bg-warning/10 text-warning px-4 py-3 text-sm flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Never signed in</p>
              <p className="text-xs mt-1 opacity-90">
                This email is in the x5-admin-users table but has not yet completed a Google
                sign-in. No Cognito record exists.
              </p>
            </div>
          </div>
        )}
        {cognitoQuery.data && cognitoQuery.data.exists && (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant={
                  cognitoQuery.data.status === 'CONFIRMED' ||
                  cognitoQuery.data.status === 'EXTERNAL_PROVIDER'
                    ? 'success'
                    : 'outline'
                }
              >
                {cognitoQuery.data.status ?? 'UNKNOWN'}
              </Badge>
              {cognitoQuery.data.enabled === false && (
                <Badge variant="destructive">DISABLED</Badge>
              )}
              {cognitoQuery.data.attributes['email_verified'] === 'true' && (
                <Badge variant="outline" className="text-[10px]">
                  email verified
                </Badge>
              )}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <CognitoRow label="Cognito username" value={<code className="font-mono text-xs">{cognitoQuery.data.username ?? '—'}</code>} />
              <CognitoRow label="Sub (identity ID)" value={<code className="font-mono text-xs">{cognitoQuery.data.attributes['sub'] ?? '—'}</code>} />
              <CognitoRow
                label="First seen (Cognito)"
                value={
                  <span title={cognitoQuery.data.userCreateDate ?? ''}>
                    {cognitoQuery.data.userCreateDate
                      ? `${formatRelative(cognitoQuery.data.userCreateDate)} · ${formatDateTime(cognitoQuery.data.userCreateDate)}`
                      : '—'}
                  </span>
                }
              />
              <CognitoRow
                label="Last activity"
                value={
                  <span title={cognitoQuery.data.userLastModifiedDate ?? ''}>
                    {cognitoQuery.data.userLastModifiedDate
                      ? `${formatRelative(cognitoQuery.data.userLastModifiedDate)} · ${formatDateTime(cognitoQuery.data.userLastModifiedDate)}`
                      : '—'}
                  </span>
                }
              />
              {cognitoQuery.data.attributes['name'] && (
                <CognitoRow label="Name" value={cognitoQuery.data.attributes['name']} />
              )}
              {cognitoQuery.data.attributes['picture'] && (
                <CognitoRow
                  label="Profile picture"
                  value={
                    <a
                      href={cognitoQuery.data.attributes['picture']}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline text-xs"
                    >
                      View
                    </a>
                  }
                />
              )}
            </dl>

            <AuthEventsSection
              events={cognitoQuery.data.authEvents}
              error={cognitoQuery.data.authEventsError}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CognitoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1">{value}</dd>
    </div>
  );
}

function AuthEventsSection({
  events,
  error,
}: {
  events: AdminUserCognitoAuthEvent[];
  error: string | null;
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
        <Shield className="h-3.5 w-3.5" />
        Auth history (last {events.length})
      </div>
      {error && (
        <div className="text-xs text-destructive mb-2">
          Auth events unavailable: {error}
        </div>
      )}
      {!error && events.length === 0 && (
        <p className="text-xs text-muted-foreground">No auth events recorded.</p>
      )}
      {events.length > 0 && (
        <div className="overflow-x-auto -mx-6">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-y border-border">
              <tr>
                <th className="px-6 py-2 font-medium text-left">When</th>
                <th className="px-3 py-2 font-medium text-left">Event</th>
                <th className="px-3 py-2 font-medium text-left">Result</th>
                <th className="px-3 py-2 font-medium text-left">Risk</th>
                <th className="px-3 py-2 font-medium text-left">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {events.map((e) => (
                <tr key={e.eventId ?? `${e.creationDate}-${e.eventType}`}>
                  <td className="px-6 py-2">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span title={e.creationDate ?? ''}>
                        {formatRelative(e.creationDate)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono">{e.eventType ?? '—'}</td>
                  <td className="px-3 py-2">
                    {e.eventResponse === 'Pass' ? (
                      <span className="inline-flex items-center gap-1 text-success">
                        <CheckCircle2 className="h-3 w-3" />
                        {e.eventResponse}
                      </span>
                    ) : e.eventResponse ? (
                      <span className="inline-flex items-center gap-1 text-destructive">
                        <XCircle className="h-3 w-3" />
                        {e.eventResponse}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {e.riskLevel ?? '—'}
                    {e.riskDecision && e.riskDecision !== 'NoRisk' && (
                      <span className="ml-1 text-[10px] opacity-80">({e.riskDecision})</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    <div className="flex flex-col gap-0.5">
                      {e.ipAddress && (
                        <span className="font-mono text-[11px]">{e.ipAddress}</span>
                      )}
                      {(e.city || e.country) && (
                        <span className="inline-flex items-center gap-1 text-[11px]">
                          <Globe className="h-3 w-3" />
                          {[e.city, e.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {e.deviceName && (
                        <span className="text-[11px] opacity-80">{e.deviceName}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function csvEscape(value: string): string {
  if (value == null) return '';
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
